import { PanelView } from 'modules/movableInk/constants';
import { useInternalPaginationContext } from 'modules/movableInk/contexts/InternalPagination';
import { useMovableInkBlockContext } from 'modules/movableInk/contexts/MovableInkBlockContext';
import { usePanelContext } from 'modules/movableInk/contexts/Panel';
import { UIEvent, useCallback, useEffect, useRef } from 'react';
import { useCampaignSelectorStickyThreshold } from './useCampaignSelectorStickyThreshold';

export const testIds = {
  container: 'mi-scrollable-list-container',
};

// the threshold at which the campaign selectors become sticky
export const CAMPAIGN_SELECTORS_STICKY_THRESHOLD = 88;

// a custom hook that handles the scroll position of the panel.
// When the user scrolls to the bottom of the panel, the next page of blocks is fetched.
export const usePanelScroll = () => {
  const { currentPage, setCurrentPage } = useInternalPaginationContext();
  const { blockPagesTotal, isFetchingBlocks } = useMovableInkBlockContext();
  const { panelView, scrollBookmark, setScrollBookmark, setIsCampaignSelectorsSticky } =
    usePanelContext();
  const scrollPosition = useRef<number>(0);
  const ref = useRef<HTMLDivElement>(null);
  const campaignSelectorStickyTreshold = useCampaignSelectorStickyThreshold();

  useEffect(() => {
    const scrollArea = ref.current;
    if (scrollBookmark && panelView === PanelView.List && scrollArea) {
      scrollArea.scrollTo(0, scrollBookmark);
    }
    return () => {
      if (panelView === PanelView.List && scrollPosition.current) {
        setScrollBookmark(scrollPosition.current);
      }
    };
  }, [setScrollBookmark, panelView, scrollBookmark]);

  return useCallback(
    (e: UIEvent<HTMLDivElement>) => {
      const eventTarget = e.target as HTMLDivElement;
      const scrollableHeight = eventTarget.scrollHeight - eventTarget.clientHeight;
      scrollPosition.current = eventTarget.scrollTop;

      const isCampaignSelectorsSticky = scrollPosition.current > campaignSelectorStickyTreshold;

      setIsCampaignSelectorsSticky(isCampaignSelectorsSticky);

      if (
        eventTarget.scrollTop >= scrollableHeight - 20 &&
        !isFetchingBlocks &&
        currentPage < blockPagesTotal
      ) {
        // within 20 px of the bottom
        setCurrentPage(currentPage + 1);
      }
    },
    [
      currentPage,
      blockPagesTotal,
      isFetchingBlocks,
      setCurrentPage,
      setIsCampaignSelectorsSticky,
      campaignSelectorStickyTreshold,
    ],
  );
};
